import {
  ChangeDetectorRef, Component, OnInit,
} from '@angular/core';
import {
  ActivatedRoute, Router,
} from '@angular/router';
import { NotificationService } from '../../../application/services/NotificationService';
import { HttpResponse } from '../../../application/types/HttpResponse';
import { ChampionshipApiService } from '../../services/championship-api/ChampionshipApiService';
import { TeamApiService } from '../../services/team-api/TeamApiService';
import {
  Championship,
  SaveTeamData,
} from '../../types/Event';

@Component({
  selector: 'app-team-form-page',
  standalone: false,
  templateUrl: './team-form-page.component.html',
  styleUrl: './team-form-page.component.sass',
})
export class TeamFormPageComponent implements OnInit {
  public teamSlug: string | null = null;
  public mode: 'create' | 'edit' = 'create';
  public championships: Championship[] = [];
  public formData: SaveTeamData = {
    name: '',
    slug: null,
    championshipId: '',
    logoUrl: null,
    city: null,
    websiteUrl: null,
    telegramUrl: null,
    vkUrl: null,
    foundationYear: null,
    arenaName: null,
    arenaCapacity: null,
  };
  public selectedLogoFile: File | null = null;
  public selectedLogoFileName: string | null = null;
  public saving = false;
  public errorMessage: string | null = null;

  public constructor(
    private route: ActivatedRoute,
    private router: Router,
    private teamApi: TeamApiService,
    private championshipApi: ChampionshipApiService,
    private notificationService: NotificationService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  public async ngOnInit(): Promise<void> {
    this.teamSlug = this.route.snapshot.paramMap.get('teamSlug');
    this.mode = this.teamSlug ? 'edit' : 'create';
    try {
      const response = await this.championshipApi.getChampionships();
      const championships = this.unwrapResponse(response);
      this.championships = championships ?? [];
      if (this.teamSlug) {
        await this.loadTeam(this.teamSlug);
      }
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Не удалось загрузить чемпионаты.';
    } finally {
      this.changeDetector.detectChanges();
    }
  }

  public selectLogoFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.selectedLogoFile = file;
    this.selectedLogoFileName = file?.name ?? null;
  }

  public async saveTeam(): Promise<void> {
    this.saving = true;
    this.errorMessage = null;

    try {
      const response = this.teamSlug
        ? await this.teamApi.updateTeam(this.teamSlug, this.formData)
        : await this.teamApi.createTeam(this.formData);
      const team = this.unwrapResponse(response);
      if (!team) {
        return;
      }

      if (this.selectedLogoFile) {
        const logoResponse = await this.teamApi.uploadTeamLogo(team.id, this.selectedLogoFile);
        if (!this.unwrapResponse(logoResponse)) {
          return;
        }
      }

      this.notificationService.notify('directory', this.mode === 'edit' ? 'Команда обновлена' : 'Команда добавлена', team.name);
      await this.router.navigate([
        '/dashboard/teams',
        team.slug ?? team.id,
      ]);
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Не удалось создать команду.';
    } finally {
      this.saving = false;
      this.changeDetector.detectChanges();
    }
  }

  public get title(): string {
    return this.mode === 'edit' ? 'Редактирование команды' : 'Новая команда';
  }

  private async loadTeam(teamSlug: string): Promise<void> {
    const response = await this.teamApi.getTeam(teamSlug);
    const team = this.unwrapResponse(response);
    if (!team) {
      this.errorMessage = 'Команда не найдена.';
      return;
    }

    this.formData = {
      name: team.name,
      slug: team.slug,
      championshipId: team.championshipId ?? '',
      logoUrl: team.logoUrl,
      city: team.city,
      websiteUrl: team.websiteUrl,
      telegramUrl: team.telegramUrl,
      vkUrl: team.vkUrl,
      foundationYear: team.foundationYear,
      arenaName: team.arenaName,
      arenaCapacity: team.arenaCapacity,
    };
  }

  private unwrapResponse<ResponseType>(response: HttpResponse<ResponseType>): ResponseType | null {
    if (response.status === 'failed') {
      this.errorMessage = response.errorMessage;
      return null;
    }

    return response.response;
  }
}
